import { cn } from "@/lib/utils";

export interface FilterTab {
  label: string;
  icon?: React.ElementType;
}

/**
 * Where the sliding indicator sits, as CSS.
 *
 * A separate pure function because jsdom's CSS parser drops a `calc()`
 * containing a division, so reading `style.left` back off the element returns
 * "" and any assertion on it passes vacuously.
 *
 * The 0.5rem is the track's `p-1` on both sides and the 0.25rem is that same
 * padding offsetting the first tab. Change one without the other and the
 * indicator drifts off its tab.
 */
export function indicatorStyle(active: number, count: number) {
  const slot = `((100% - 0.5rem) / ${count})`;
  return {
    width: `calc${slot}`,
    left: `calc(${active} * ${slot} + 0.25rem)`,
  };
}

export function FilterTabs({
  tabs,
  active,
  onChange,
  className,
}: {
  tabs: FilterTab[];
  active: number;
  onChange: (index: number) => void;
  className?: string;
}) {
  return (
    <div
      role="tablist"
      className={cn(
        "relative flex w-full max-w-md rounded-lg border border-border bg-muted/40 p-1",
        className,
      )}
    >
      <div
        aria-hidden
        className="absolute inset-y-1 rounded-md bg-primary shadow-sm transition-[left] duration-300 ease-out"
        style={indicatorStyle(active, tabs.length)}
      />
      {tabs.map((t, i) => (
        <button
          key={t.label}
          type="button"
          role="tab"
          aria-selected={i === active}
          onClick={() => onChange(i)}
          className={cn(
            // min-w-0 is load-bearing: the labels are whitespace-nowrap, so
            // without it the tabs sum to more than a phone is wide.
            "relative z-10 flex min-w-0 flex-1 items-center justify-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium transition-colors sm:px-3",
            i === active
              ? "text-primary-foreground"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {t.icon && <t.icon className="hidden h-4 w-4 sm:block" />}
          <span className="whitespace-nowrap">{t.label}</span>
        </button>
      ))}
    </div>
  );
}
